import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Slide from '@mui/material/Slide';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import { Map as ImmutableMap } from 'immutable';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';

import { documentsActions, documentsSelectors, documentsTypes } from 'app-components/flux/documents';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import { DIALOG_TYPE_EMPTY_DIALOG } from 'components/Dialogs/EmptyDialog';
import QButton from 'components/QButton';
import BoxField from 'components/Transactions/BoxField';
import {
  MAX_ATTACHMENTS_PER_TRANSACTION,
  getDocumentAttachments,
  getReceiptAttachments,
} from 'app-components/flux/transactions/transactionsTypes';

const useStyles = makeStyles()((theme) => ({
  input: {
    minWidth: '0 !important',
  },
  chip: {
    maxWidth: 'calc(100% - 74px)',
  },
  boxChip: {
    maxWidth: 'calc(100% - 34px)',
  },
  iconContainer: {
    marginBottom: '2%',
    flexDirection: 'column',
    display: 'flex',
    justifyContent: 'flex-end',
    height: '100%',
  },
  uploadButton: {
    position: 'absolute',
    right: 0,
    bottom: 0,
  },
  uploadIcon: {
    marginBottom: 2,
    marginRight: 4,
  },
  boxRoot: {
    paddingRight: '86px !important',
    flexWrap: 'wrap',
  },
  boxInput: {
    flexBasis: 0,
  },
  dropZonePlaceholder: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.default,
    opacity: 0.8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    border: `4px dashed ${theme.palette.border.main}`,
    borderRadius: 8,
    zIndex: theme.zIndex.drawer,
  },
  statusImage: {
    width: 128,
    height: 128,
    marginTop: 24,
    marginBottom: 24,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

function preventTextKeys(e) {
  if (e.key !== 'Enter' && e.key !== 'Escape' && e.key !== 'Tab' && e.key !== 'Backspace') {
    e.preventDefault();
  }
}

const AttachmentsField = (props) => {
  const {
    attachments,
    onChange,
    textFieldProps,
    dropzoneRef,
    onDropzoneChanged,
    readOnly,
    disableUpload,
    box,
    showDropZone,
    inputProps,
    ...autocompleteProps
  } = props;

  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const dropzone = useDropzone();
  const { acceptedFiles, isDragActive } = dropzone;

  const businessEnabled = useSelector(entitlementsSelectors.getBusinessEnabled);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onClick, onFocus, onBlur, onKeyDown, tabIndex, ...dropzoneRootProps } = dropzone.getRootProps();
  useImperativeHandle(dropzoneRef, () => dropzone, [dropzone]);
  useEffect(() => {
    onDropzoneChanged?.();
  }, [isDragActive, onDropzoneChanged]);

  // Receipt attachments are displayed/managed by the receipt UI, not this field. Only Storage
  // (document) attachments are shown here, and receipt entries are merged back into every
  // onChange emission so they're never dropped when document attachments change.
  const documentAttachments = useMemo(
    () => (attachments ? getDocumentAttachments(attachments) : attachments),
    [attachments],
  );
  const receiptAttachments = useMemo(() => getReceiptAttachments(attachments), [attachments]);
  const emitChange = useCallback(
    (event, newDocumentAttachments, reason, details) => {
      onChange?.(event, [...(newDocumentAttachments || []), ...receiptAttachments], reason, details);
    },
    [onChange, receiptAttachments],
  );

  const documents = useSelector((state) => documentsSelectors.getDocumentsForAttachments(state, documentAttachments));
  useEffect(() => {
    if (onChange) {
      const updatedAttachments = documentAttachments?.map((attachment) => {
        const attachmentFound = documents.find(
          (document) =>
            document.id === attachment.id ||
            (document.clientId && attachment.clientId && document.clientId === attachment.clientId),
        );
        const id = attachmentFound?.id || attachment.id;
        return {
          ...attachment,
          ...(id && { id }),
        };
      });
      if (!_.isEqual(documentAttachments, updatedAttachments) && !(!documentAttachments && !updatedAttachments)) {
        emitChange(undefined, updatedAttachments);
      }
    }
  }, [documents, documentAttachments, onChange, emitChange]);

  const prevAcceptedFilesRef = useRef();
  const [uploadingClientIds, setUploadingClientIds] = useState([]);
  const [errorClientIds, setErrorClientIds] = useState([]);
  useEffect(() => {
    if (!_.isEqual(prevAcceptedFilesRef.current, acceptedFiles) && acceptedFiles.length) {
      prevAcceptedFilesRef.current = acceptedFiles;
      if ((documentAttachments?.length || 0) + (acceptedFiles.length || 0) > MAX_ATTACHMENTS_PER_TRANSACTION) {
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'max-attachemnts-limit',
              type: DIALOG_CONFIRMATION,
              allowNesting: true,
              props: ImmutableMap(
                mkConfirmationDialogProps({
                  title: 'Too many attachments',
                  content: `Sorry, you can only add ${MAX_ATTACHMENTS_PER_TRANSACTION} items per transaction.`,
                  confirmLabel: 'Got it',
                  denyLabel: null,
                }),
              ),
            }),
          ),
        );
      }
      const acceptedAttachemnts = acceptedFiles
        .slice(0, MAX_ATTACHMENTS_PER_TRANSACTION - (documentAttachments?.length || 0))
        .map((file) => {
          const clientId = uuidv4().toUpperCase();
          setUploadingClientIds((uploadingClientIdsCurrent) => [...uploadingClientIdsCurrent, clientId]);
          new Promise((resolve, reject) =>
            dispatch(
              documentsActions.uploadDocument(
                {
                  clientId,
                  document: documentsTypes.mkDocument({
                    name: file.name,
                    documentType: 'OTHER',
                    contentType: file.type || 'application/octet-stream',
                    clientId,
                    size: file.size,
                  }),
                  data: file,
                },
                { resolve, reject },
              ),
            ),
          )
            .then((_response) => {
              setUploadingClientIds((uploadingClientIdsCurrent) =>
                uploadingClientIdsCurrent.filter((uploadingClientId) => uploadingClientId !== clientId),
              );
              setErrorClientIds((errorClientIdsCurrent) =>
                errorClientIdsCurrent.filter((errorClientId) => errorClientId !== clientId),
              );
            })
            .catch((_error) => {
              setUploadingClientIds((uploadingClientIdsLast) =>
                uploadingClientIdsLast.filter((uploadingClientId) => uploadingClientId !== clientId),
              );
              setErrorClientIds((errorClientIdsCurrent) => [...errorClientIdsCurrent, clientId]);
            });
          return { clientId, name: file.name };
        });
      emitChange(undefined, [...(documentAttachments || []), ...acceptedAttachemnts]);
    }
  }, [acceptedFiles, attachments, documentAttachments, emitChange, dispatch]);

  const placeholderText = businessEnabled
    ? 'Upload receipts and other documentation for your transaction'
    : 'Add your attachment';

  const renderInput = (params) => {
    let ret;
    if (box) {
      ret = (
        <BoxField
          {...autocompleteProps}
          {...params}
          label='Attachments'
          placeholder=' '
          readOnly={readOnly || disableUpload}
          startAdornment={params.InputProps.startAdornment}
          onKeyDown={preventTextKeys}
          endAdornment={
            <QButton
              id='attach-button'
              onClick={() => dropzone.open()}
              aria-label='add attachment'
              className={classes.uploadButton}
            >
              <CloudUploadIcon className={classes.uploadIcon} /> UPLOAD
            </QButton>
          }
          classes={{ root: classes.boxRoot, input: classes.boxInput }}
        />
      );
    } else {
      ret = (
        <TextField
          {...params}
          sharedcomponentid='ATTACHMENTS_FIELD'
          size={props.size}
          label='Attachments'
          placeholder={documentAttachments?.length ? null : placeholderText}
          margin='normal'
          variant='filled'
          fullWidth
          // inputProps={{
          //   className: classNames(params.inputProps?.className, classes.input),
          // }}
          InputProps={{
            ...params.InputProps,
            readOnly: true,
            ...(readOnly ? { disableUnderline: readOnly } : undefined),
            startAdornment: [
              ...(readOnly || disableUpload
                ? []
                : [
                    <InputAdornment key='attachment-icon' position='start' className={classes.iconContainer}>
                      <IconButton
                        id='attach-button'
                        onClick={() => dropzone.open()}
                        aria-label='add attachment'
                        size='medium'
                      >
                        <AttachFileIcon />
                      </IconButton>
                    </InputAdornment>,
                  ]),
              ...(params.InputProps.startAdornment || []),
            ],
            inputProps: {
              ...params.inputProps,
              readOnly: true,
              className: cx(params.inputProps?.className, classes.input),
            },
            ...inputProps,
          }}
          {...textFieldProps}
        />
      );
    }

    return ret;
  };

  return (
    <>
      <input {...dropzone.getInputProps()} />
      <Autocomplete
        {...dropzoneRootProps}
        id='attachments'
        multiple
        options={documentAttachments || []}
        value={documentAttachments || []}
        getOptionLabel={(attachment) =>
          attachment.name || documents?.find((myDocument) => myDocument.id === attachment.id)?.name
        }
        onChange={(event, newValue, reason, details) => {
          if (!readOnly) {
            emitChange(event, newValue, reason, details);
            // Only Storage attachments are rendered here, so removals only ever delete
            // Storage documents; receipt attachments are never deleted through this API.
            if (reason === 'removeOption' && details.option.id) {
              dispatch(documentsActions.deleteDocument(documentsTypes.mkDocument({ id: details.option.id })));
            }
          }
        }}
        open={false}
        disableClearable
        forcePopupIcon={false}
        size={props.size}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <Chip
                size={props.size}
                key={option.id || option.clientId}
                label={option.name || documents?.find((myDocument) => myDocument.id === option.id)?.name}
                {...tagProps}
                className={cx(tagProps.className, box ? classes.boxChip : classes.chip)}
                disabled={uploadingClientIds.includes(option.clientId)}
                {...(uploadingClientIds.includes(option.clientId) && { deleteIcon: <CircularProgress size={20} /> })}
                {...(readOnly && { onDelete: null })}
                icon={
                  errorClientIds.includes(option.clientId) ? (
                    <Tooltip title="Can't upload your attachment">
                      <ErrorOutlineIcon />
                    </Tooltip>
                  ) : undefined
                }
                onClick={() => {
                  if (option.id) {
                    new Promise((resolve, reject) =>
                      dispatch(
                        documentsActions.getDocument(documentsTypes.mkDocument({ id: option.id }), { resolve, reject }),
                      ),
                    )
                      .then((result) => {
                        if (assert(result?.data?.url, "can't get attachment from the server")) {
                          const popup = window.open(result.data.url, result.data.name, 'width=800,height=600');
                          if (!popup) {
                            dispatch(
                              rootUiActions.createDialog(
                                rootUiTypes.mkRootUiData({
                                  id: 'show-attachment',
                                  type: DIALOG_TYPE_EMPTY_DIALOG,
                                  allowNesting: true,
                                  props: ImmutableMap({
                                    title: `${result.data.name} (enable browser pop ups to download)`,
                                    maxWidth: 'lg',
                                    content: (
                                      <iframe title={result.data.name} width={800} height={600} src={result.data.url} />
                                    ),
                                  }),
                                }),
                              ),
                            );
                          }
                        }
                      })
                      .catch((error) => assert(false, error));
                  }
                }}
              />
            );
          }) || []
        }
        renderInput={renderInput}
        {...autocompleteProps}
      />
      <Zoom in={showDropZone && isDragActive} mountOnEnter unmountOnExit>
        <div className={classes.dropZonePlaceholder}>
          <Slide in={showDropZone && isDragActive} direction='up'>
            <div>
              <CloudUploadIcon className={classes.statusImage} />
              Drag & Drop to upload
            </div>
          </Slide>
        </div>
      </Zoom>
    </>
  );
};

AttachmentsField.propTypes = {
  textFieldProps: PropTypes.object,
  attachments: PropTypes.array,
  onChange: PropTypes.func,
  dropzoneRef: PropTypes.object,
  onDropzoneChanged: PropTypes.func,
  readOnly: PropTypes.bool,
  size: PropTypes.string,
  box: PropTypes.bool,
  showDropZone: PropTypes.bool,
  inputProps: PropTypes.object,
  disableUpload: PropTypes.bool,
};

export default React.memo(AttachmentsField);
